import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import Footerdiv from "../layout/footer.js";
import Headerdiv from "../layout/header.js";
import {
    Layout,
    Typography,
    Form,
    Input,
    Select,
    Button,
    Table,
    Card,
    Row,
    Col,
    Space,
    Tag,
    theme,
    Popconfirm,
    Breadcrumb,
    Modal,
    message,
    Descriptions
} from 'antd';
import axios from 'axios';
import { AlertError, AlertSuccess } from '../usableclasses.js';

const { Content } = Layout;
const { Option } = Select;

const PendingCancels = () => {
    const navigate = useNavigate();
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const USERID = localStorage.getItem("USERID");

    const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

    const [cancelRequests, setCancelRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);

    const [isActionModalVisible, setIsActionModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionType, setActionType] = useState('');

    const fetchCancelRequests = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${ApiURL}/orders/getallpendingcancelrequests`, {
                headers: { SystemAccessToken: TOKEN }
            });

            if (response.data && Array.isArray(response.data)) {
                setCancelRequests(response.data);
                setTotalRecords(response.data.length);
            } else {
                setCancelRequests([]);
                setTotalRecords(0);
                AlertError("Failed to load cancel requests: Invalid data format.");
            }
        } catch (error) {
            console.error('Error fetching cancel requests:', error);
            AlertError('Failed to fetch cancel requests. Please try again later.');
            setCancelRequests([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCancelRequests();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        } catch (e) {
            return dateString;
        }
    };

    const getOrderStatusTag = (status) => {
        let color = 'default';
        switch (status?.toUpperCase()) {
            case 'SUBMITTED':
                color = 'cyan';
                break;
            case 'UNPAID':
                color = 'orange';
                break;
            case 'PAYMENT FAILED':
                color = 'volcano';
                break;
            case 'PAID':
                color = 'green';
                break;
            case 'PROCESSING':
                color = 'blue';
                break;
            case 'ORDER READY':
                color = 'purple';
                break;
            case 'COMPLETED':
                color = 'geekblue';
                break;
            case 'CANCELLED':
            case 'CANCELED':
                color = 'red';
                break;
            default:
                color = 'default';
        }
        return <Tag color={color}>{status?.toUpperCase() || 'N/A'}</Tag>;
    };

    const handleAction = async () => {
        if (!selectedRequest || !actionType) return;

        setLoading(true);
        try {
            const payload = {
                cancelReqId: selectedRequest.cancelReqId.toString(),
                orderId: selectedRequest.orderId.toString(),
                updatedBy: USERID,
                approvalStatus: actionType.toUpperCase(),
            };

            // The endpoint for updating the request status was likely incorrect.
            const response = await axios.post(`${ApiURL}/orders/pendingcancelrequestupdate`, payload, {
                headers: { SystemAccessToken: TOKEN }
            });

            if (response.data === true) {
                AlertSuccess(`Order cancel request ${(actionType || '').toLowerCase()} successfully.`);
                fetchCancelRequests();
            } else {
                AlertError(`Failed to ${(actionType || '').toLowerCase()} order cancel request.`);
                console.error(`Action failed for ${actionType}:`, response.data);
            }
        } catch (error) {
            console.error(`Error ${(actionType || '').toLowerCase()} cancel request:`, error);
            AlertError(`An error occurred while trying to ${(actionType || '').toLowerCase()} the request.`);
        } finally {
            setLoading(false);
            setIsActionModalVisible(false);
            setSelectedRequest(null);
            setActionType('');
        }
    };

    const openActionModal = (request, type) => {
        setSelectedRequest(request);
        setActionType(type);
        setIsActionModalVisible(true);
    };

    const handleModalCancel = () => {
        setIsActionModalVisible(false);
        setSelectedRequest(null);
        setActionType('');
    };

    const columns = [
        {
            title: 'Order Reference',
            dataIndex: 'orderRef',
            key: 'orderRef',
        },
        {
            title: 'Order Status',
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            render: (status) => getOrderStatusTag(status),
        },
        {
            title: 'Is Paid',
            dataIndex: 'isPaid',
            key: 'isPaid',
            render: (isPaid) => <Tag color={isPaid ? 'green' : 'red'}>{isPaid ? 'Yes' : 'No'}</Tag>,
        },
        {
            title: 'Is Canceled',
            dataIndex: 'isCanceled',
            key: 'isCanceled',
            render: (isCanceled) => <Tag color={isCanceled ? 'red' : 'green'}>{isCanceled ? 'Yes' : 'No'}</Tag>,
        },
        {
            title: 'Request Date/Time',
            dataIndex: 'reqDateTime',
            key: 'reqDateTime',
            render: (text) => formatDate(text),
        },
        {
            title: 'Requested By',
            dataIndex: 'requestedUser',
            key: 'requestedUser',
        },
        {
            title: 'Request Reason',
            dataIndex: 'canceledReason',
            key: 'canceledReason',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => openActionModal(record, 'APPROVED')}
                        disabled={loading}
                    >
                        APPROVE
                    </Button>
                    <Button
                        type="primary"
                        danger
                        size="small"
                        onClick={() => openActionModal(record, 'REJECTED')}
                        disabled={loading}
                    >
                        REJECT
                    </Button>
                </Space>
            ),
        },
    ];

    const paginationConfig = {
        current: currentPage,
        pageSize: pageSize,
        total: totalRecords,
        onChange: (page) => setCurrentPage(page),
        showSizeChanger: false,
        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`
    };

    return (
        <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
            <Headerdiv />
            <Content style={{ padding: '10px' }}>
                <Breadcrumb style={{ margin: "16px 0" }}>
                    <Breadcrumb.Item><Link to={'/orders'}>Orders</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Cancel Requests</Breadcrumb.Item>
                </Breadcrumb>

                <Card title="Order Cancel Requests">
                    <div style={{ overflowX: 'auto' }}>
                        <Table
                            columns={columns}
                            dataSource={cancelRequests}
                            pagination={paginationConfig}
                            rowKey="cancelReqId"
                            size="middle"
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                            locale={{
                                emptyText: loading ? 'Loading...' : (
                                    <div>
                                        <p>No pending cancel requests found.</p>
                                    </div>
                                )
                            }}
                        />
                    </div>
                </Card>
            </Content>
            <Footerdiv />

            <Modal
                title={`${actionType || ''} Order Cancel Request`}
                open={isActionModalVisible}
                onOk={handleAction}
                onCancel={handleModalCancel}
                okText={actionType || 'Confirm'}
                cancelText="Cancel"
                confirmLoading={loading}
            >
                <p>
                    Are you sure you want to {(actionType || '').toLowerCase()} this request for Order <b>{selectedRequest?.orderRef}</b>?
                </p>
                {selectedRequest && (
                    <Descriptions bordered column={1} size="small" style={{ marginTop: 16 }}>
                        <Descriptions.Item label="Cancel Request ID">{selectedRequest.cancelReqId}</Descriptions.Item>
                        <Descriptions.Item label="Order Reference">
                            <Link to={`/orders/view/${selectedRequest.orderId}`}>{selectedRequest.orderRef}</Link>
                        </Descriptions.Item>
                        <Descriptions.Item label="Order Status">{getOrderStatusTag(selectedRequest.orderStatus)}</Descriptions.Item>
                        <Descriptions.Item label="Is Paid"><Tag color={selectedRequest.isPaid ? 'green' : 'red'}>{selectedRequest.isPaid ? 'Yes' : 'No'}</Tag></Descriptions.Item>
                        <Descriptions.Item label="Request Date">{formatDate(selectedRequest.reqDateTime)}</Descriptions.Item>
                        <Descriptions.Item label="Requested By">{selectedRequest.requestedUser}</Descriptions.Item>
                        <Descriptions.Item label="Reason for Cancellation">{selectedRequest.canceledReason}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </Layout>
    );
};

export default PendingCancels;
